import { InversionContainer } from '@/controller/container'
import { InjectionTokens } from '@/controller/tokens'

import { UniqueEntityID } from '@/domain/core'
import { UseCase } from '@/domain/core/UseCase'
import { Checklist, ChecklistItem, ChecklistProcedure } from '@/domain/models/Checklist'
import { Result } from '@/domain/protocols/Result'

import type { IChecklistRepository } from '@/data/ChecklistRepository/IChecklistRepository'

import { I18n } from '@/i18n/I18n'

// `name` is a varchar(120) on the server and overflowing it answers 500.
export const CHECKLIST_NAME_MAX_LENGTH = 120

const escapeRegExp = (value: string): string => {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

const getChecklistBaseName = (name: string, copyLabel: string): string => {
  const copyLabelRegex = escapeRegExp(copyLabel)
  const pattern = new RegExp(`^(.*) \\(${copyLabelRegex}(?: (\\d+))?\\)$`)
  const match = name.match(pattern)

  if (!match) {
    return name
  }

  return match[1]
}

const getChecklistCopyIndex = (name: string, baseName: string, copyLabel: string): number | null => {
  const copyLabelRegex = escapeRegExp(copyLabel)
  const baseNameRegex = escapeRegExp(baseName)
  const pattern = new RegExp(`^${baseNameRegex} \\(${copyLabelRegex}(?: (\\d+))?\\)$`)
  const match = name.match(pattern)

  if (!match) {
    return null
  }

  if (!match[1]) {
    return 1
  }

  const parsedIndex = Number.parseInt(match[1], 10)
  if (Number.isNaN(parsedIndex) || parsedIndex < 1) {
    return 1
  }

  return parsedIndex
}

const buildCopyName = (baseName: string, copyIndex: number, copyLabel: string): string => {
  const copySuffix = copyIndex === 1 ? ` (${copyLabel})` : ` (${copyLabel} ${copyIndex})`

  // The base name is what gets cut, never the suffix: truncating the suffix would make two copies
  // land on the same name.
  const availableLength = CHECKLIST_NAME_MAX_LENGTH - copySuffix.length
  if (availableLength <= 0) return copySuffix.trim()

  return `${baseName.slice(0, availableLength).trimEnd()}${copySuffix}`
}

export const buildDuplicatedChecklistName = (name: string, existingNames: string[], copyLabel: string): string => {
  const baseName = getChecklistBaseName(name, copyLabel)

  const currentHighestCopyIndex = existingNames.reduce((highest, existingName) => {
    const copyIndex = getChecklistCopyIndex(existingName, baseName, copyLabel)

    if (!copyIndex) {
      return highest
    }

    return Math.max(highest, copyIndex)
  }, 0)

  const takenNames = new Set(existingNames)
  let nextCopyIndex = currentHighestCopyIndex + 1
  let duplicatedName = buildCopyName(baseName, nextCopyIndex, copyLabel)

  // Truncation can collapse two different indexes onto the same 120-char name, so the index keeps
  // advancing until the name is free. With no truncation the first candidate is always free and the
  // result matches the pilotapp exactly.
  while (takenNames.has(duplicatedName)) {
    nextCopyIndex += 1
    duplicatedName = buildCopyName(baseName, nextCopyIndex, copyLabel)
  }

  return duplicatedName
}

const duplicateChecklistItem = (item: ChecklistItem): ChecklistItem => {
  const duplicatedId = new UniqueEntityID().toString()

  if (item.type === 'divider') {
    return {
      id: duplicatedId,
      type: 'divider',
      title: item.title
    }
  }

  if (item.type === 'check') {
    return {
      id: duplicatedId,
      type: 'check',
      title: item.title,
      action: item.action,
      description: item.description
    }
  }

  return {
    id: duplicatedId,
    type: 'note',
    title: item.title,
    description: item.description
  }
}

const duplicateChecklistProcedure = (procedure: ChecklistProcedure): ChecklistProcedure => {
  return {
    id: new UniqueEntityID().toString(),
    name: procedure.name,
    type: procedure.type,
    items: (procedure.items ?? []).map(duplicateChecklistItem)
  }
}

export class DuplicateChecklistUseCase implements UseCase<Checklist, Promise<Result<Checklist>>> {
  private repository: IChecklistRepository
  private i18n: I18n

  constructor(repository?: IChecklistRepository, i18n?: I18n) {
    const container = InversionContainer.getInstance().getContainer()
    this.repository = repository ?? container.get<IChecklistRepository>(InjectionTokens.ChecklistRepository)
    this.i18n = i18n ?? container.get<I18n>(InjectionTokens.I18n)
  }

  async execute(checklist: Checklist): Promise<Result<Checklist>> {
    try {
      if (!checklist?.id || !checklist?.userAircraftId) {
        return Result.fail('CHECKLIST_INVALID')
      }

      const existingChecklistsResult = await this.repository.getChecklists()
      if (existingChecklistsResult.isFailure) {
        return Result.fail(existingChecklistsResult.error)
      }

      const duplicatedChecklistName = buildDuplicatedChecklistName(
        checklist.name ?? this.i18n.resolveToken('CHECKLISTS_PROCEDURES_UNNAMED'),
        existingChecklistsResult.getValue().map((existingChecklist) => existingChecklist.name ?? ''),
        this.i18n.resolveToken('CHECKLISTS_COMMON_COPY')
      )

      const now = new Date()
      const duplicatedChecklist = Checklist.create(
        {
          userAircraftId: checklist.userAircraftId,
          name: duplicatedChecklistName,
          procedures: (checklist.procedures ?? []).map(duplicateChecklistProcedure),
          createdAt: now,
          updatedAt: now
        },
        new UniqueEntityID()
      )

      const saveResult = await this.repository.saveChecklist(duplicatedChecklist)
      if (saveResult.isFailure) {
        return Result.fail(saveResult.error)
      }

      return Result.ok(duplicatedChecklist)
    } catch (error) {
      return Result.fail(error)
    }
  }
}
